import React, { useEffect, useMemo } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams, useNavigate } from 'react-router-dom';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from './components/ui/sonner';
import i18n from './i18n';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import { TranslationsProvider } from './context/TranslationsContext';
import { UrlMappingsProvider, useUrlMappings } from './context/UrlMappingsContext';
import { AdminAuthProvider } from './contexts/AdminAuthContext';
import { FitnetProvider } from './contexts/FitnetContext';
import FloatingNavbar from './components/FloatingNavbar';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';
import BackendHealthCheck from './components/BackendHealthCheck';
import { useDocumentMeta } from './hooks/useDocumentMeta';

// Pages
import HomePage from './pages/HomePage';
import BookingPage from './pages/BookingPage';
import { LoginPage, RegisterPage } from './pages/AuthPages';
import AuthCallback from './pages/AuthCallback';
import AccountPage from './pages/AccountPage';
import FAQPage from './pages/FAQPage';
import ContactPage from './pages/ContactPage';
import PassesPage from './pages/PassesPage';
import PricingPage from './pages/PricingPage';
import ParkPage from './pages/ParkPage';
import ActivitiesPage from './pages/ActivitiesPage';
import PaymentStatusPage from './pages/PaymentStatusPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import EventsPage from './pages/EventsPage';
import EventDetailPage from './pages/EventDetailPage';
import ActivateGiftPassPage from './pages/ActivateGiftPassPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import PendingVerificationPage from './pages/PendingVerificationPage';

// Digital Signage
import DSScreen1 from './pages/ds/DSScreen1';
import DSScreen2 from './pages/ds/DSScreen2';
import DSScreen3 from './pages/ds/DSScreen3';

// Admin Pages
import AdminLogin from './pages/admin/AdminLogin';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminHero from './pages/admin/AdminHero';
import AdminSlider from './pages/admin/AdminSlider';
import AdminNews from './pages/admin/AdminNews';
import AdminFAQ from './pages/admin/AdminFAQ';
import AdminHours from './pages/admin/AdminHours';
import AdminPricing from './pages/admin/AdminPricing';
import AdminPasses from './pages/admin/AdminPasses';
import AdminDiscounts from './pages/admin/AdminDiscounts';
import AdminInstructors from './pages/admin/AdminInstructors';
import AdminTranslations from './pages/admin/AdminTranslations';
import AdminLanguages from './pages/admin/AdminLanguages';
import AdminPark from './pages/admin/AdminPark';
import AdminClasses from './pages/admin/AdminClasses';
import AdminUsers from './pages/admin/AdminUsers';
import AdminNotifications from './pages/admin/AdminNotifications';
import AdminDocuments from './pages/admin/AdminDocuments';
import AdminBlog from './pages/admin/AdminBlog';
import AdminEvents from './pages/admin/AdminEvents';
import AdminEmailTemplates from './pages/admin/AdminEmailTemplates';
import AdminFitnetTranslations from './pages/admin/AdminFitnetTranslations';
import AdminLoyalty from './pages/admin/AdminLoyalty';
import AdminCookieConsent from './pages/admin/AdminCookieConsent';
import AdminServiceAvailability from './pages/admin/AdminServiceAvailability';
import AdminSortOrder from './pages/admin/AdminSortOrder';
import AdminPhonePrefixes from './pages/admin/AdminPhonePrefixes';
import AdminCustomers from './pages/admin/AdminCustomers';
import AdminBirthdayEmails from './pages/admin/AdminBirthdayEmails';
import DocumentPage from './pages/DocumentPage';
import TabletRegistrationPage from './pages/TabletRegistrationPage';
import TabletRegistrationV2 from './pages/TabletRegistrationV2';

import './index.css';

// Helper to build localized link (for use in components) - now uses dynamic context
export const useLocalizedPath = () => {
  const { i18n } = useTranslation();
  const { getLocalizedPath } = useUrlMappings();
  return (key, params = '') => {
    const base = getLocalizedPath(key, i18n.language);
    return params ? `${base}/${params}` : base;
  };
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Language wrapper - sets i18n language based on URL + updates document meta
const LanguageWrapper = ({ children }) => {
  const { lang } = useParams();
  const { i18n } = useTranslation();
  
  useEffect(() => {
    // Accept any 2-letter language code
    if (lang && lang.length === 2 && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  // Update document title, meta description, and <html lang>
  useDocumentMeta();
  
  return children;
};

// Auto-detect language and redirect to appropriate version
const LanguageDetector = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLanguageSupported, defaultLang } = useUrlMappings();
  
  useEffect(() => {
    // Get browser language or saved preference
    const browserLang = navigator.language?.split('-')[0];
    const savedLang = localStorage.getItem('i18nextLng');
    
    // Determine target language - allow any saved 2-letter code
    let targetLang = defaultLang;
    if (savedLang && savedLang.length === 2 && isLanguageSupported(savedLang)) {
      targetLang = savedLang;
    } else if (browserLang && browserLang.length === 2 && isLanguageSupported(browserLang)) {
      targetLang = browserLang;
    }
    
    // Redirect to language-prefixed version
    const newPath = `/${targetLang}${location.pathname === '/' ? '' : location.pathname}${location.search}`;
    navigate(newPath, { replace: true });
  }, [navigate, location, isLanguageSupported, defaultLang]);
  
  return null;
};

// Legacy URL redirects - maps old URLs to new localized ones
const LegacyRedirect = ({ routeKey }) => {
  const { getMappings, defaultLang } = useUrlMappings();
  const savedLang = localStorage.getItem('i18nextLng');
  // Accept any saved language
  const targetLang = (savedLang && savedLang.length === 2) ? savedLang : defaultLang;
  const location = useLocation();
  const mappings = getMappings(targetLang);
  
  // Handle document paths with slugs
  if (routeKey.startsWith('document/')) {
    const slug = routeKey.replace('document/', '');
    const docPath = mappings?.document || 'document';
    return <Navigate to={`/${targetLang}/${docPath}/${slug}${location.search}`} replace />;
  }
  
  const path = mappings?.[routeKey] || routeKey;
  // Preserve query params during redirect!
  return <Navigate to={`/${targetLang}/${path}${location.search}`} replace />;
};

// Localized routes component - generates routes dynamically from URL mappings
const LocalizedRoutes = () => {
  const { lang } = useParams();
  const location = useLocation();
  const { urlMappings, loading } = useUrlMappings();

  // Collect all unique paths for each route from all languages
  const allPaths = useMemo(() => {
    const paths = {};
    const routeKeys = [
      'park', 'book', 'prices', 'passes', 'contact', 'login', 'register', 
      'account', 'activities', 'classes', 'birthdays', 'physio', 'document',
      'forgotPassword', 'resetPassword', 'paymentStatus', 'blog', 'events'
    ];
    
    routeKeys.forEach(key => {
      paths[key] = new Set();
      Object.values(urlMappings).forEach(mappings => {
        if (mappings[key]) {
          paths[key].add(mappings[key]);
        }
      });
      // Add default English paths as fallback
      const defaults = {
        park: 'park', book: 'book', prices: 'prices', passes: 'passes',
        contact: 'contact', login: 'login', register: 'register', account: 'account',
        activities: 'activities', classes: 'activities/classes', birthdays: 'activities/birthdays',
        physio: 'activities/physio', document: 'document', forgotPassword: 'forgot-password',
        resetPassword: 'reset-password', paymentStatus: 'payment/status', blog: 'blog', events: 'events'
      };
      if (defaults[key]) paths[key].add(defaults[key]);
    });
    
    return paths;
  }, [urlMappings]);

  // Helper to create routes for all language variants of a path
  const createRoutes = (routeKey, element) => {
    const paths = allPaths[routeKey] || new Set();
    return Array.from(paths).map(path => (
      <Route key={`${routeKey}-${path}`} path={path} element={element} />
    ));
  };
  
  // Handle auth callback - AFTER hooks
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <LanguageWrapper>
      <ScrollToTop />
      <FloatingNavbar />
      <main className="min-h-screen">
        <Routes>
          {/* Home */}
          <Route index element={<HomePage />} />
          
          {/* Park - all language variants */}
          {createRoutes('park', <ParkPage />)}
          
          {/* Book - all language variants */}
          {Array.from(allPaths.book || new Set(['book'])).map(path => (
            <React.Fragment key={`book-${path}`}>
              <Route path={path} element={<BookingPage />} />
              <Route path={`${path}/:disciplineSlug`} element={<BookingPage />} />
            </React.Fragment>
          ))}
          
          {/* Prices - all language variants */}
          {createRoutes('prices', <PricingPage />)}
          
          {/* Passes - all language variants */}
          {createRoutes('passes', <PassesPage />)}
          
          {/* FAQ - universal path */}
          <Route path="faq" element={<FAQPage />} />
          
          {/* Contact - all language variants */}
          {createRoutes('contact', <ContactPage />)}
          
          {/* Auth - all language variants */}
          {createRoutes('login', <LoginPage />)}
          {createRoutes('register', <RegisterPage />)}
          <Route path="auth/callback" element={<AuthCallback />} />
          
          {/* Email Verification - common paths for all languages */}
          <Route path="weryfikuj-email" element={<VerifyEmailPage />} />
          <Route path="verify-email" element={<VerifyEmailPage />} />
          <Route path="oczekuje-na-weryfikacje" element={<PendingVerificationPage />} />
          <Route path="pending-verification" element={<PendingVerificationPage />} />
          
          {/* Account - all language variants */}
          {createRoutes('account', <AccountPage />)}
          
          {/* Activities - all language variants */}
          {createRoutes('activities', <ActivitiesPage />)}
          {createRoutes('classes', <ActivitiesPage />)}
          {createRoutes('birthdays', <ActivitiesPage />)}
          {createRoutes('physio', <ActivitiesPage />)}
          
          {/* Documents - all language variants with slug */}
          {Array.from(allPaths.document || new Set(['document', 'dokument'])).map(path => (
            <Route key={`doc-${path}`} path={`${path}/:slug`} element={<DocumentPage />} />
          ))}
          
          {/* Password reset - all language variants */}
          {createRoutes('forgotPassword', <ForgotPasswordPage />)}
          {createRoutes('resetPassword', <ResetPasswordPage />)}
          
          {/* Payment - all language variants */}
          {createRoutes('paymentStatus', <PaymentStatusPage />)}
          
          {/* Blog - all language variants */}
          {Array.from(allPaths.blog || new Set(['blog'])).map(path => (
            <React.Fragment key={`blog-${path}`}>
              <Route path={path} element={<BlogPage />} />
              <Route path={`${path}/:slug`} element={<BlogPostPage />} />
              <Route path={`${path}/kategoria/:category`} element={<BlogPage />} />
              <Route path={`${path}/category/:category`} element={<BlogPage />} />
            </React.Fragment>
          ))}

          {/* Events */}
          {Array.from(allPaths.events || new Set(['events'])).map(path => (
            <React.Fragment key={`events-${path}`}>
              <Route path={path} element={<EventsPage />} />
              <Route path={`${path}/:slug`} element={<EventDetailPage />} />
            </React.Fragment>
          ))}
          
          {/* Gift Pass Activation */}
          <Route path="aktywuj-karnet" element={<ActivateGiftPassPage />} />
          <Route path="activate-pass" element={<ActivateGiftPassPage />} />
          
          {/* Legacy document redirects within language */}
          <Route path="terms" element={<Navigate to={`/${lang}/dokument/regulamin-serwisu`} replace />} />
          <Route path="privacy" element={<Navigate to={`/${lang}/dokument/polityka-prywatnosci`} replace />} />
          <Route path="skatepark-rules" element={<Navigate to={`/${lang}/dokument/regulamin-skateparku`} replace />} />
        </Routes>
      </main>
      <Footer />
      <Toaster position="top-right" />
    </LanguageWrapper>
  );
};

// Admin Router - separate from main app (no language prefix)
const AdminRouter = () => {
  return (
    <AdminAuthProvider>
      <Routes>
        <Route path="login" element={<AdminLogin />} />
        <Route element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="hero" element={<AdminHero />} />
          <Route path="slider" element={<AdminSlider />} />
          <Route path="news" element={<AdminNews />} />
          <Route path="faq" element={<AdminFAQ />} />
          <Route path="hours" element={<AdminHours />} />
          <Route path="pricing" element={<AdminPricing />} />
          <Route path="passes" element={<AdminPasses />} />
          <Route path="discounts" element={<AdminDiscounts />} />
          <Route path="instructors" element={<AdminInstructors />} />
          <Route path="translations" element={<AdminTranslations />} />
          <Route path="languages" element={<AdminLanguages />} />
          <Route path="park" element={<AdminPark />} />
          <Route path="classes" element={<AdminClasses />} />
          <Route path="documents" element={<AdminDocuments />} />
          <Route path="blog" element={<AdminBlog />} />
          <Route path="events" element={<AdminEvents />} />
          <Route path="email-templates" element={<AdminEmailTemplates />} />
          <Route path="fitnet-translations" element={<AdminFitnetTranslations />} />
          <Route path="loyalty" element={<AdminLoyalty />} />
          <Route path="cookie-consent" element={<AdminCookieConsent />} />
          <Route path="service-availability" element={<AdminServiceAvailability />} />
          <Route path="sort-order" element={<AdminSortOrder />} />
          <Route path="phone-prefixes" element={<AdminPhonePrefixes />} />
          <Route path="customers" element={<AdminCustomers />} />
          <Route path="birthday-emails" element={<AdminBirthdayEmails />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="notifications" element={<AdminNotifications />} />
        </Route>
      </Routes>
    </AdminAuthProvider>
  );
};

function App() {
  return (
    <HelmetProvider>
      <I18nextProvider i18n={i18n}>
        <ThemeProvider>
          <BackendHealthCheck>
          <AuthProvider>
            <FitnetProvider>
              <TranslationsProvider>
                <UrlMappingsProvider>
                  <BrowserRouter>
                  <Routes>
                    {/* Tablet registration - standalone, no navigation */}
                    <Route path="/tablet" element={<TabletRegistrationPage />} />
                    <Route path="/tablet-registration" element={<TabletRegistrationPage />} />
                    <Route path="/tabletv2" element={<TabletRegistrationV2 />} />
                    
                    {/* Digital Signage - standalone, no navigation */}
                    <Route path="/ds/screen1" element={<DSScreen1 />} />
                    <Route path="/ds/screen2" element={<DSScreen2 />} />
                    <Route path="/ds/screen3" element={<DSScreen3 />} />
                    
                    {/* Admin panel - no language prefix */}
                    <Route path="/admin/*" element={<AdminRouter />} />
                    
                    {/* Language-prefixed routes */}
                    <Route path="/:lang/*" element={<LocalizedRoutes />} />
                    
                    {/* Legacy redirects for backward compatibility */}
                    <Route path="/book" element={<LegacyRedirect routeKey="book" />} />
                    <Route path="/prices" element={<LegacyRedirect routeKey="prices" />} />
                    <Route path="/passes" element={<LegacyRedirect routeKey="passes" />} />
                    <Route path="/contact" element={<LegacyRedirect routeKey="contact" />} />
                    <Route path="/login" element={<LegacyRedirect routeKey="login" />} />
                    <Route path="/register" element={<LegacyRedirect routeKey="register" />} />
                    <Route path="/account" element={<LegacyRedirect routeKey="account" />} />
                    <Route path="/faq" element={<LegacyRedirect routeKey="faq" />} />
                    <Route path="/park" element={<LegacyRedirect routeKey="park" />} />
                    <Route path="/activities/*" element={<LegacyRedirect routeKey="activities" />} />
                    <Route path="/document/:slug" element={<LegacyRedirect routeKey="document" />} />
                    <Route path="/terms" element={<LegacyRedirect routeKey="document/regulamin-serwisu" />} />
                    <Route path="/privacy" element={<LegacyRedirect routeKey="document/polityka-prywatnosci" />} />
                    <Route path="/skatepark-rules" element={<LegacyRedirect routeKey="document/regulamin-skateparku" />} />
                    <Route path="/forgot-password" element={<LegacyRedirect routeKey="forgotPassword" />} />
                    <Route path="/reset-password" element={<LegacyRedirect routeKey="resetPassword" />} />
                    <Route path="/payment/status" element={<LegacyRedirect routeKey="paymentStatus" />} />
                    <Route path="/blog" element={<LegacyRedirect routeKey="blog" />} />
                    
                    {/* Root - auto-detect language and redirect */}
                    <Route path="/" element={<LanguageDetector />} />
                    
                    {/* Catch-all - redirect to home with language detection */}
                    <Route path="*" element={<LanguageDetector />} />
                  </Routes>
                </BrowserRouter>
                <CookieConsent />
                </UrlMappingsProvider>
              </TranslationsProvider>
            </FitnetProvider>
          </AuthProvider>
          </BackendHealthCheck>
        </ThemeProvider>
      </I18nextProvider>
    </HelmetProvider>
  );
}

export default App;
